import type { CatalogData } from '@/types';
import { trace, createTrace, newRunId as generateRunId } from '@/lib/logging';

export type McRunStatus = 'idle' | 'running' | 'aborted';

export type McCatalogSnapshot = {
  key: string;
  hash: string;
  version: number;
  catalog: CatalogData;
};

export type McRunProgress = {
  doneTrials: number;
  totalTrials: number;
  ratio: number;
  updatedAt: number;
};

export type McStoreState = {
  runId: string | null;
  status: McRunStatus;
  startedAt: number | null;
  progress: McRunProgress | null;
  abortController: AbortController | null;
  frozen: boolean;
  catalogSnapshot: McCatalogSnapshot | null;
};

export type McStoreSubscriber = (state: McStoreState) => void;

export type McStartOptions = {
  runId?: string;
  totalTrials?: number;
};

export type McRunHandle = {
  runId: string;
  signal: AbortSignal;
  abortController: AbortController;
  isStale: () => boolean;
  updateProgress: (progress: { doneTrials: number; totalTrials: number }) => boolean;
  settle: (patch?: Partial<McStoreState>) => boolean;
  complete: () => boolean;
};

const createInitialState = (): McStoreState => ({
  runId: null,
  status: 'idle',
  startedAt: null,
  progress: null,
  abortController: null,
  frozen: false,
  catalogSnapshot: null,
});

let state: McStoreState = createInitialState();
const subscribers = new Set<McStoreSubscriber>();

const cloneProgress = (progress: McRunProgress | null): McRunProgress | null =>
  progress ? { ...progress } : null;

const snapshot = (): McStoreState => ({
  ...state,
  progress: cloneProgress(state.progress),
  catalogSnapshot: state.catalogSnapshot
    ? {
        ...state.catalogSnapshot,
        catalog: state.catalogSnapshot.catalog,
      }
    : null,
});

const notify = () => {
  const current = snapshot();
  for (const subscriber of subscribers) {
    subscriber(current);
  }
};

const patchState = (patch: Partial<McStoreState>) => {
  state = {
    ...state,
    ...patch,
    progress:
      patch.progress === undefined
        ? state.progress
        : cloneProgress(patch.progress ?? null),
    catalogSnapshot:
      patch.catalogSnapshot === undefined
        ? state.catalogSnapshot
        : patch.catalogSnapshot ?? null,
    frozen: patch.frozen ?? state.frozen,
  };
  notify();
};

const clamp01 = (value: number) => {
  if (!Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
};

const normalizeTrials = (value: number) => {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.floor(value);
};

const createRunId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `mc-${crypto.randomUUID()}`;
  }
  return `mc-${Math.random().toString(36).slice(2, 10)}`;
};

export const mcStore = {
  subscribe(subscriber: McStoreSubscriber) {
    subscribers.add(subscriber);
    subscriber(snapshot());
    return () => {
      subscribers.delete(subscriber);
    };
  },

  getState(): McStoreState {
    return snapshot();
  },

  start(options?: McStartOptions): McRunHandle {
    if (state.abortController && !state.abortController.signal.aborted) {
      state.abortController.abort('superseded');
      trace(createTrace(state.runId ?? 'unknown', 'mcStore', 'mc-fast', 'Previous run superseded'));
    }

    const runId = options?.runId ?? createRunId();
    const abortController = new AbortController();
    const totalTrials =
      typeof options?.totalTrials === 'number' ? normalizeTrials(options.totalTrials) : null;

    trace(createTrace(runId, 'mcStore', 'mc-fast', 'Starting new MC run', {
      totalTrials,
      hasCustomRunId: Boolean(options?.runId),
    }));

    patchState({
      runId,
      status: 'running',
      startedAt: Date.now(),
      abortController,
      frozen: true,
      progress:
        typeof totalTrials === 'number'
          ? {
              doneTrials: 0,
              totalTrials,
              ratio: 0,
              updatedAt: Date.now(),
            }
          : null,
    });

    const isStale = () => state.runId !== runId;

    const settle = (patch?: Partial<McStoreState>) => {
      if (isStale()) return false;
      patchState({
        ...createInitialState(),
        status: patch?.status ?? 'idle',
        ...patch,
      });
      return true;
    };

    const complete = () => settle();

    const updateProgress = ({
      doneTrials,
      totalTrials: incomingTotal,
    }: {
      doneTrials: number;
      totalTrials: number;
    }) => {
      if (isStale()) return false;
      const normalizedTotal = normalizeTrials(incomingTotal);
      const normalizedDone = normalizeTrials(doneTrials);
      const ratio =
        normalizedTotal > 0
          ? clamp01(normalizedDone / normalizedTotal)
          : normalizedDone > 0
          ? 1
          : 0;

      if (normalizedDone % 100 === 0 || ratio >= 1) {
        trace(createTrace(runId, 'mcStore', 'mc-fast', 'Progress update', {
          doneTrials: normalizedDone,
          totalTrials: normalizedTotal,
          ratio: Math.round(ratio * 100),
        }));
      }

      patchState({
        progress: {
          doneTrials: normalizedDone,
          totalTrials: normalizedTotal,
          ratio,
          updatedAt: Date.now(),
        },
      });
      return true;
    };

    return {
      runId,
      signal: abortController.signal,
      abortController,
      isStale,
      updateProgress,
      settle,
      complete,
    };
  },

  abort(reason?: unknown) {
    const { abortController, runId: currentRunId } = state;
    if (!abortController) {
      return false;
    }
    
    trace(createTrace(currentRunId ?? 'unknown', 'mcStore', 'error', 'Run aborted', {
      reason: String(reason),
    }));

    if (!abortController.signal.aborted) {
      abortController.abort(reason);
    }
    patchState({
      ...createInitialState(),
      status: 'aborted',
    });
    return true;
  },

  setFrozen(frozen: boolean) {
    if (state.frozen === frozen) {
      return;
    }
    patchState({
      frozen,
      catalogSnapshot: frozen ? state.catalogSnapshot : null,
    });
  },

  setCatalogSnapshot(snapshot: McCatalogSnapshot | null) {
    patchState({ catalogSnapshot: snapshot });
  },
};
